
import React from 'react';
import { Badge } from "@/components/ui/badge";
import { User, Clock } from "lucide-react";
import { format } from "date-fns";

interface ClientInteraction {
  id: string;
  type: 'email' | 'call' | 'meeting' | 'note' | 'document';
  title: string;
  content?: string;
  date: Date;
  user: string;
  status?: 'completed' | 'scheduled' | 'cancelled';
  priority?: 'high' | 'medium' | 'low';
}

interface InteractionItemProps {
  interaction: ClientInteraction;
}

const getInteractionIcon = (type: string) => {
  const icons = {
    email: '📧',
    call: '📞',
    meeting: '🎥',
    note: '💬',
    document: '📄'
  };
  return icons[type as keyof typeof icons] || '💬';
};

const getInteractionColor = (type: string) => {
  switch (type) {
    case 'email': return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'call': return 'bg-green-100 text-green-700 border-green-200';
    case 'meeting': return 'bg-purple-100 text-purple-700 border-purple-200';
    case 'note': return 'bg-gray-100 text-gray-700 border-gray-200';
    case 'document': return 'bg-orange-100 text-orange-700 border-orange-200';
    default: return 'bg-gray-100 text-gray-700 border-gray-200';
  }
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'high': return 'bg-red-100 text-red-700';
    case 'medium': return 'bg-yellow-100 text-yellow-700';
    case 'low': return 'bg-green-100 text-green-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

export const InteractionItem: React.FC<InteractionItemProps> = ({ interaction }) => (
  <div className="border rounded-lg p-4 space-y-3 hover:bg-gray-50 transition-colors">
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-full text-sm ${getInteractionColor(interaction.type)}`}>
          {getInteractionIcon(interaction.type)}
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-sm">{interaction.title}</h4>
          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
            <User className="h-3 w-3" />
            {interaction.user}
            <Clock className="h-3 w-3 ml-2" />
            {format(interaction.date, 'MMM d, yyyy h:mm a')}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {interaction.priority && (
          <Badge variant="outline" className={`text-xs ${getPriorityColor(interaction.priority)}`}>
            {interaction.priority}
          </Badge>
        )}
        <Badge className={`text-xs ${getInteractionColor(interaction.type)}`}>
          {interaction.type}
        </Badge>
      </div>
    </div>
    
    {interaction.content && (
      <p className="text-sm text-muted-foreground pl-11">
        {interaction.content}
      </p>
    )}
    
    {interaction.status === 'scheduled' && (
      <div className="pl-11">
        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
          Scheduled
        </Badge>
      </div>
    )}
  </div>
);
